// Global image optimizer — auto-applies to EVERY <img> on the site, including
// pages/components created in the future. Rewrites optimizable image URLs
// (our /api/media proxy + Unsplash/Pexels) to a right-sized webp variant.
// Mounted once in App; a MutationObserver keeps new/changed images covered.
import { img } from "@/lib/img";

function isOptimizable(src) {
  return !!src && (src.includes("/api/media/") || src.includes("images.unsplash.com") || src.includes("images.pexels.com"));
}

function alreadyOptimized(src) {
  return src.includes("fmt=webp") ||
    (src.includes("unsplash") && src.includes("auto=format")) ||
    (src.includes("pexels") && src.includes("auto=compress"));
}

function processImg(el) {
  if (el.dataset && el.dataset.noOpt === "1") return;
  const src = el.getAttribute("src");
  if (!isOptimizable(src) || alreadyOptimized(src)) return;
  const rect = el.getBoundingClientRect();
  const w = Math.round(rect.width) || el.clientWidth || 800;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const target = Math.max(160, Math.min(Math.ceil((w * dpr) / 100) * 100, 1600));
  const optimized = img(src, target);
  if (optimized !== src) {
    if (!el.hasAttribute("loading")) el.setAttribute("loading", "lazy");
    if (!el.hasAttribute("decoding")) el.setAttribute("decoding", "async");
    el.setAttribute("src", optimized);
  }
}

function scan(root) {
  if (root.nodeType !== 1) return;
  if (root.tagName === "IMG") processImg(root);
  if (root.querySelectorAll) root.querySelectorAll("img").forEach(processImg);
}

export function installImageOptimizer() {
  if (typeof window === "undefined" || window.__imgOptInstalled) return () => {};
  window.__imgOptInstalled = true;
  scan(document.body);
  const obs = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "childList") {
        m.addedNodes.forEach(scan);
      } else if (m.type === "attributes" && m.target.tagName === "IMG") {
        processImg(m.target);
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
  return () => { obs.disconnect(); window.__imgOptInstalled = false; };
}
